import { AxiosResponse } from "axios";
import { API } from "../index";

type BooleanQueryParam = boolean | "true" | "false";

export type VisibilityTarget = "web" | "pos" | "kiosk";

export type MenuCategoryIconKey =
  | "all"
  | "cake"
  | "chicken"
  | "coffee"
  | "cup"
  | "dessert"
  | "drink"
  | "flame"
  | "leaf"
  | "meat"
  | "pizza"
  | "side"
  | "utensils";

export type MenuCategory = {
  id: number;
  site: number;
  name: string;
  icon_key?: MenuCategoryIconKey | null;
  display_order: number;
  is_active: boolean;
  is_web_visible: boolean;
  is_pos_visible: boolean;
  is_kiosk_visible: boolean;
  created_at: string;
  updated_at: string;
};

export type MenuOption = {
  id: number;
  option_group: number;
  name: string;
  extra_price: number;
  tax_amount: number;
  supply_amount: number;
  tax_free_amount: number;
  tip_amount: number;
  display_order: number;
  is_active: boolean;
};

export type MenuOptionGroup = {
  id: number;
  menu: number;
  name: string;
  is_required: boolean;
  min_select: number;
  max_select: number;
  display_order: number;
  is_active: boolean;
  options?: MenuOption[];
};

export type Menu = {
  id: number;
  site: number;
  category: number;
  name: string;
  description: string;
  price: number;
  tax_amount: number;
  supply_amount: number;
  tax_free_amount: number;
  tip_amount: number;
  is_sold_out: boolean;
  display_order: number;
  is_active: boolean;
  is_web_visible: boolean;
  is_pos_visible: boolean;
  is_kiosk_visible: boolean;
  allow_pos_price_override: boolean;
  fulfillment_type: "INSTANT" | "KITCHEN";
  image?: string | null;
  option_groups?: MenuOptionGroup[];
};

export type MenuCategoryListParams = {
  is_active?: BooleanQueryParam;
  visibility_target?: VisibilityTarget;
  page?: number;
  page_size?: number;
};

export type MenuListParams = {
  category_id?: number;
  is_active?: BooleanQueryParam;
  include_sold_out?: BooleanQueryParam;
  visibility_target?: VisibilityTarget;
  page?: number;
  page_size?: number;
};

export type TableGroup = {
  id: number;
  site: number;
  name: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type ServiceTable = {
  id: number;
  site: number;
  group: number | null;
  group_name: string | null;
  code: string;
  name: string;
  capacity: number;
  display_order: number;
  is_active: boolean;
};

export type TableOrderItemOption = {
  id: number;
  menu_option_id: number | null;
  option_name_snapshot: string;
  extra_price_snapshot: number;
  tax_amount_snapshot: number;
  supply_amount_snapshot: number;
  tax_free_amount_snapshot: number;
  tip_amount_snapshot: number;
  quantity: number;
};

export type TableOrderItem = {
  id: number;
  menu_id: number;
  menu_name_snapshot: string;
  unit_price_snapshot: number;
  quantity: number;
  line_amount: number;
  options: TableOrderItemOption[];
};

export type CustomerRegistrationState = "NONE" | "EXISTING_USER" | "AUTO_CREATED_USER";
export type CustomerPhoneCaptureMode = "CRM_DIRECT" | "TOSS_FRONT" | "SKIP";
export type CustomerPhoneCaptureDetail = "NONE" | "MANUAL" | "KEYPAD" | "QR";
export type TableOrderSource = "WEB_CUSTOMER" | "CRM_POS" | "UNKNOWN";

export type TableOrder = {
  id: number;
  site: number;
  table: number | null;
  table_name: string | null;
  table_code: string | null;
  created_by: number;
  created_by_phone: string;
  customer_user: number | null;
  customer_phone_num: string;
  customer_name: string;
  customer_registration_state: CustomerRegistrationState;
  customer_phone_capture_mode: CustomerPhoneCaptureMode;
  customer_phone_capture_detail: CustomerPhoneCaptureDetail;
  customer_phone_captured_at: string | null;
  order: number | null;
  order_source: TableOrderSource;
  order_type: "serving" | "pickup";
  fulfillment_type: "INSTANT" | "KITCHEN" | null;
  status: "NEW" | "ACCEPTED" | "SERVED" | "CANCELED";
  payment_status:
    | "UNPAID"
    | "PAYMENT_PENDING"
    | "PAID"
    | "PAYMENT_FAILED"
    | "CANCEL_PENDING"
    | "CANCELED";
  memo: string;
  total_amount: number;
  payment_requested_at: string | null;
  paid_at: string | null;
  payment_failed_at: string | null;
  payment_failure_reason: string;
  canceled_payment_at: string | null;
  accepted_at: string | null;
  served_at: string | null;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
  items: TableOrderItem[];
};

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export type TableOrderListParams = {
  status?: TableOrder["status"] | "" | string;
  order_type?: TableOrder["order_type"] | "";
  fulfillment_type?: TableOrder["fulfillment_type"] | "";
  payment_status?: TableOrder["payment_status"] | "" | string;
  order_source?: TableOrder["order_source"] | "" | string;
  customer_phone?: string;
  customer_name?: string;
  customer_registration_state?: CustomerRegistrationState | "";
  table_id?: number | string | "";
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
};

export type ReorderResponse = {
  updated: number;
};

const BASE = "/api/v2";
const MAX_PAGE_SIZE = 100;

const fetchAllPages = async <T>(
  path: string,
  params?: Record<string, any>
): Promise<PaginatedResponse<T>> => {
  const allResults: T[] = [];
  let nextUrl: string | null = null;
  let page = 1;

  while (true) {
    let response: AxiosResponse<PaginatedResponse<T>>;
    if (nextUrl) {
      response = await API.get(nextUrl);
    } else {
      response = await API.get(path, {
        params: {
          ...(params || {}),
          page,
          page_size: MAX_PAGE_SIZE,
        },
      });
    }

    const data = response.data;
    allResults.push(...(data.results || []));

    if (!data.next) break;
    nextUrl = data.next;
    page += 1;
  }

  return {
    count: allResults.length,
    next: null,
    previous: null,
    results: allResults,
  };
};

export const tableOrderService = {
  listMenuCategories: (params?: MenuCategoryListParams): Promise<AxiosResponse<PaginatedResponse<MenuCategory>>> =>
    API.get(`${BASE}/menu-categories/`, { params: { ...(params || {}), page_size: MAX_PAGE_SIZE } }),
  listAllMenuCategories: (params?: MenuCategoryListParams): Promise<PaginatedResponse<MenuCategory>> =>
    fetchAllPages<MenuCategory>(`${BASE}/menu-categories/`, params),
  getMenuCategory: (id: number): Promise<AxiosResponse<MenuCategory>> =>
    API.get(`${BASE}/menu-categories/${id}/`),

  createMenuCategory: (payload: Partial<MenuCategory>): Promise<AxiosResponse<MenuCategory>> =>
    API.post(`${BASE}/menu-categories/`, payload),

  updateMenuCategory: (id: number, payload: Partial<MenuCategory>): Promise<AxiosResponse<MenuCategory>> =>
    API.patch(`${BASE}/menu-categories/${id}/`, payload),

  deleteMenuCategory: (id: number): Promise<AxiosResponse<void>> =>
    API.delete(`${BASE}/menu-categories/${id}/`),

  listMenus: (params?: MenuListParams): Promise<AxiosResponse<PaginatedResponse<Menu>>> =>
    API.get(`${BASE}/menus/`, { params: { ...(params || {}), page_size: MAX_PAGE_SIZE } }),
  listAllMenus: (params?: MenuListParams): Promise<PaginatedResponse<Menu>> =>
    fetchAllPages<Menu>(`${BASE}/menus/`, params),
  getMenu: (id: number): Promise<AxiosResponse<Menu>> => API.get(`${BASE}/menus/${id}/`),

  createMenu: (payload: Partial<Menu>): Promise<AxiosResponse<Menu>> =>
    API.post(`${BASE}/menus/`, payload),

  createMenuWithImage: (payload: Partial<Menu>, imageFile: File): Promise<AxiosResponse<Menu>> => {
    const formData = new FormData();
    formData.append("category", String(payload.category ?? ""));
    formData.append("name", payload.name ?? "");
    formData.append("description", payload.description ?? "");
    formData.append("price", String(payload.price ?? 0));
    formData.append("tax_amount", String(payload.tax_amount ?? 0));
    formData.append("supply_amount", String(payload.supply_amount ?? 0));
    formData.append("tax_free_amount", String(payload.tax_free_amount ?? 0));
    formData.append("tip_amount", String(payload.tip_amount ?? 0));
    formData.append("display_order", String(payload.display_order ?? 0));
    formData.append("is_active", String(payload.is_active ?? true));
    formData.append("is_sold_out", String(payload.is_sold_out ?? false));
    formData.append("is_web_visible", String(payload.is_web_visible ?? true));
    formData.append("is_pos_visible", String(payload.is_pos_visible ?? true));
    formData.append("is_kiosk_visible", String(payload.is_kiosk_visible ?? true));
    formData.append("allow_pos_price_override", String(payload.allow_pos_price_override ?? false));
    formData.append("fulfillment_type", String(payload.fulfillment_type ?? "KITCHEN"));
    formData.append("image", imageFile);

    return API.post(`${BASE}/menus/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  updateMenu: (id: number, payload: Partial<Menu>): Promise<AxiosResponse<Menu>> =>
    API.patch(`${BASE}/menus/${id}/`, payload),

  reorderMenus: (ids: number[]): Promise<AxiosResponse<ReorderResponse>> =>
    API.post(`${BASE}/menus/reorder/`, { ids }),

  updateMenuWithImage: (id: number, payload: Partial<Menu>, imageFile: File): Promise<AxiosResponse<Menu>> => {
    const formData = new FormData();
    formData.append("category", String(payload.category ?? ""));
    formData.append("name", payload.name ?? "");
    formData.append("description", payload.description ?? "");
    formData.append("price", String(payload.price ?? 0));
    formData.append("tax_amount", String(payload.tax_amount ?? 0));
    formData.append("supply_amount", String(payload.supply_amount ?? 0));
    formData.append("tax_free_amount", String(payload.tax_free_amount ?? 0));
    formData.append("tip_amount", String(payload.tip_amount ?? 0));
    formData.append("display_order", String(payload.display_order ?? 0));
    formData.append("is_active", String(payload.is_active ?? true));
    formData.append("is_sold_out", String(payload.is_sold_out ?? false));
    formData.append("is_web_visible", String(payload.is_web_visible ?? true));
    formData.append("is_pos_visible", String(payload.is_pos_visible ?? true));
    formData.append("is_kiosk_visible", String(payload.is_kiosk_visible ?? true));
    formData.append("allow_pos_price_override", String(payload.allow_pos_price_override ?? false));
    formData.append("fulfillment_type", String(payload.fulfillment_type ?? "KITCHEN"));
    formData.append("image", imageFile);

    return API.patch(`${BASE}/menus/${id}/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  deleteMenu: (id: number): Promise<AxiosResponse<void>> =>
    API.delete(`${BASE}/menus/${id}/`),

  listMenuOptionGroups: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<MenuOptionGroup>>> =>
    API.get(`${BASE}/menu-option-groups/`, { params: { ...(params || {}), page_size: MAX_PAGE_SIZE } }),
  listAllMenuOptionGroups: (params?: Record<string, any>): Promise<PaginatedResponse<MenuOptionGroup>> =>
    fetchAllPages<MenuOptionGroup>(`${BASE}/menu-option-groups/`, params),

  createMenuOptionGroup: (payload: Partial<MenuOptionGroup>): Promise<AxiosResponse<MenuOptionGroup>> =>
    API.post(`${BASE}/menu-option-groups/`, payload),

  updateMenuOptionGroup: (id: number, payload: Partial<MenuOptionGroup>): Promise<AxiosResponse<MenuOptionGroup>> =>
    API.patch(`${BASE}/menu-option-groups/${id}/`, payload),

  deleteMenuOptionGroup: (id: number): Promise<AxiosResponse<void>> =>
    API.delete(`${BASE}/menu-option-groups/${id}/`),

  listMenuOptions: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<MenuOption>>> =>
    API.get(`${BASE}/menu-options/`, { params: { ...(params || {}), page_size: MAX_PAGE_SIZE } }),
  listAllMenuOptions: (params?: Record<string, any>): Promise<PaginatedResponse<MenuOption>> =>
    fetchAllPages<MenuOption>(`${BASE}/menu-options/`, params),

  createMenuOption: (payload: Partial<MenuOption>): Promise<AxiosResponse<MenuOption>> =>
    API.post(`${BASE}/menu-options/`, payload),

  updateMenuOption: (id: number, payload: Partial<MenuOption>): Promise<AxiosResponse<MenuOption>> =>
    API.patch(`${BASE}/menu-options/${id}/`, payload),

  deleteMenuOption: (id: number): Promise<AxiosResponse<void>> =>
    API.delete(`${BASE}/menu-options/${id}/`),

  listTableGroups: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<TableGroup>>> =>
    API.get(`${BASE}/table-groups/`, { params: { ...(params || {}), page_size: MAX_PAGE_SIZE } }),
  listAllTableGroups: (params?: Record<string, any>): Promise<PaginatedResponse<TableGroup>> =>
    fetchAllPages<TableGroup>(`${BASE}/table-groups/`, params),
  getTableGroup: (id: number): Promise<AxiosResponse<TableGroup>> =>
    API.get(`${BASE}/table-groups/${id}/`),

  createTableGroup: (payload: Partial<TableGroup>): Promise<AxiosResponse<TableGroup>> =>
    API.post(`${BASE}/table-groups/`, payload),

  updateTableGroup: (id: number, payload: Partial<TableGroup>): Promise<AxiosResponse<TableGroup>> =>
    API.patch(`${BASE}/table-groups/${id}/`, payload),

  deleteTableGroup: (id: number): Promise<AxiosResponse<void>> =>
    API.delete(`${BASE}/table-groups/${id}/`),

  listServiceTables: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<ServiceTable>>> =>
    API.get(`${BASE}/service-tables/`, { params: { ...(params || {}), page_size: MAX_PAGE_SIZE } }),
  listAllServiceTables: (params?: Record<string, any>): Promise<PaginatedResponse<ServiceTable>> =>
    fetchAllPages<ServiceTable>(`${BASE}/service-tables/`, params),
  getServiceTable: (id: number): Promise<AxiosResponse<ServiceTable>> =>
    API.get(`${BASE}/service-tables/${id}/`),

  createServiceTable: (payload: Partial<ServiceTable>): Promise<AxiosResponse<ServiceTable>> =>
    API.post(`${BASE}/service-tables/`, payload),

  updateServiceTable: (id: number, payload: Partial<ServiceTable>): Promise<AxiosResponse<ServiceTable>> =>
    API.patch(`${BASE}/service-tables/${id}/`, payload),

  deleteServiceTable: (id: number): Promise<AxiosResponse<void>> =>
    API.delete(`${BASE}/service-tables/${id}/`),

  listTableOrders: (params?: TableOrderListParams): Promise<AxiosResponse<PaginatedResponse<TableOrder>>> =>
    API.get(`${BASE}/table-orders/`, { params: { page_size: MAX_PAGE_SIZE, ...(params || {}) } }),

  getTableOrder: (id: number): Promise<AxiosResponse<TableOrder>> =>
    API.get(`${BASE}/table-orders/${id}/`),

  updateTableOrderStatus: (
    id: number,
    status: "NEW" | "ACCEPTED" | "SERVED" | "CANCELED"
  ): Promise<AxiosResponse<TableOrder>> => API.patch(`${BASE}/table-orders/${id}/status/`, { status }),
};
